<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import { DropdownSection, DropdownItem } from 'next/dropdown-menu/base';

const props = defineProps({
  accounts: {
    type: Array,
    default: () => [],
  },
  currentAccountId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();
const searchQuery = ref('');
const searchInput = ref(null);

onMounted(() => searchInput.value?.focus());

const filteredAccounts = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return props.accounts;
  return props.accounts.filter(account =>
    account.name.toLowerCase().includes(query)
  );
});
</script>

<template>
  <div class="px-1 pt-1">
    <div
      class="flex items-center gap-2 px-2 h-8 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand"
    >
      <span
        aria-hidden="true"
        class="i-lucide-search size-4 text-n-slate-10 flex-shrink-0"
      />
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="search"
        autocomplete="off"
        class="w-full h-full text-sm bg-transparent border-0 outline-none reset-base text-n-slate-12 placeholder:text-n-slate-10"
        :placeholder="t('SIDEBAR_ITEMS.SEARCH_ACCOUNT')"
        :aria-label="t('SIDEBAR_ITEMS.SEARCH_ACCOUNT')"
      />
    </div>
  </div>
  <DropdownSection
    :title="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')"
    class="max-h-80 overflow-y-auto"
  >
    <DropdownItem
      v-for="account in filteredAccounts"
      :id="`account-${account.id}`"
      :key="account.id"
      class="cursor-pointer"
      @click="emit('select', account.id)"
    >
      <template #label>
        <div
          :for="account.name"
          class="text-left rtl:text-right flex gap-2 items-center"
        >
          <span
            class="text-n-slate-12 max-w-36 truncate min-w-0"
            :title="account.name"
          >
            {{ account.name }}
          </span>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <span
            class="text-n-slate-11 max-w-24 truncate capitalize"
            :title="account.name"
          >
            {{
              account.custom_role_id ? account.custom_role.name : account.role
            }}
          </span>
        </div>
        <Icon
          v-show="account.id === currentAccountId"
          icon="i-lucide-check"
          class="text-n-teal-11 size-5"
        />
      </template>
    </DropdownItem>
    <li
      v-if="!filteredAccounts.length"
      class="px-2 py-2 text-sm text-n-slate-11"
    >
      {{ t('SIDEBAR_ITEMS.NO_ACCOUNT_FOUND') }}
    </li>
  </DropdownSection>
</template>
